import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import { WSButton, WSList, WSText } from "@wingspanhq/fe-component-library";
import { useEffect, useRef, useState } from "react";
import { useWSMutation } from "@ws-react-query";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { footprintAppearanceConfig } from "../../../shared/constants/footprint";

type Props = { token: string; onSubmitted: () => Promise<void> };

export const TaxCorrectionDocuments: React.FC<Props> = ({
  token,
  onSubmitted
}) => {
  const [error, setError] = useState<unknown>();
  const [submitted, setSubmitted] = useState(false);
  const callback = useRef(onSubmitted);
  callback.current = onSubmitted;
  const component = useRef<ReturnType<typeof footprint.init>>();
  const active = useRef(true);

  const [finish, finishMeta] = useWSMutation(async () => {
    setError(undefined);
    await callback.current();
  });

  useEffect(() => {
    active.current = true;
    try {
      component.current = footprint.init({
        kind: FootprintComponentKind.Verify,
        variant: "modal",
        authToken: token,
        options: { showLogo: false },
        appearance: footprintAppearanceConfig,
        onComplete: () => {
          if (!active.current) return;
          setSubmitted(true);
          void finish();
        },
        onError: error => {
          if (active.current) setError(error);
        }
      });
      component.current.render();
    } catch (error) {
      setError(error);
    }
    return () => {
      active.current = false;
      component.current?.destroy();
    };
    // Initialize once per token; callback is kept current separately.
  }, [token, finish]);

  return (
    <WSList gap="L">
      <WSText.ParagraphSm>
        {submitted
          ? "Your documents have been submitted."
          : "Complete the document request to submit your verification."}
      </WSText.ParagraphSm>
      <WSErrorMessage
        contextKey="MonitoredEntity"
        error={finishMeta.error || error}
      />
      <WSButton
        loading={finishMeta.isLoading}
        onClick={() =>
          submitted ? void finish() : component.current?.render()
        }
      >
        {submitted ? "Continue" : "Open document request"}
      </WSButton>
    </WSList>
  );
};
